import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import DevelopLayout from '../../components/layout/DevelopLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Code,
  Rocket,
  Share2,
  Bot,
  Lock,
  Zap,
  Database,
  GitBranch,
  RefreshCw
} from 'lucide-react';
import { cn } from '../../lib/utils';

// =============================================
// DEVELOP - Zone de développement V2
// =============================================
// Utilise DevelopLayout avec sidebar dédiée
// =============================================

const DevelopPage = () => {
  const { isAgency } = useAuth();
  const navigate = useNavigate();

  // Redirect non-agency users
  if (!isAgency) {
    navigate('/dashboard/agency');
    return null;
  }

  const v2Features = [
    {
      title: 'Copilote',
      description: 'Cellule d\'accompagnement 360° avec stratégie marketing, ROI mesurable et planning automatisé',
      icon: Rocket,
      status: 'development',
      progress: 40,
      items: ['Dashboard ROI', 'Missions', 'Planning', 'Rapports']
    },
    {
      title: 'Réseaux Sociaux',
      description: 'Gestion et planification des publications sur les réseaux sociaux',
      icon: Share2,
      status: 'planned',
      progress: 10,
      items: ['Calendrier éditorial', 'Automatisation', 'Analytics']
    },
    {
      title: 'Assistant IA',
      description: 'Assistant intelligent pour automatiser les tâches récurrentes',
      icon: Bot,
      status: 'planned',
      progress: 5,
      items: ['Génération de contenu', 'Réponses automatiques', 'Analyse']
    },
    {
      title: 'Outils Marketing',
      description: 'Suite d\'outils pour optimiser vos campagnes',
      icon: Zap,
      status: 'partial',
      progress: 60,
      items: ['Suivi de liens', 'QR Codes', 'Campagnes']
    },
  ];

  const statusConfig = {
    development: { label: 'En développement', color: 'bg-amber-100 text-amber-700', icon: Code },
    planned: { label: 'Planifié', color: 'bg-slate-100 text-slate-600', icon: Lock },
    partial: { label: 'Partiellement disponible', color: 'bg-emerald-100 text-emerald-700', icon: Zap },
  };

  return (
    <DevelopLayout>
      <div className="max-w-5xl mx-auto">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Vue d'ensemble V2</h1>
          <p className="text-slate-500">État d'avancement des fonctionnalités en développement</p>
        </div>

        {/* Migration Info */}
        <Card className="mb-8 bg-gradient-to-r from-violet-500 to-purple-500 text-white border-0">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                  <RefreshCw className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-bold text-lg">Migration V1 → V2</h3>
                  <p className="text-white/80 text-sm">
                    Quand la V2 sera prête, vous pourrez basculer avec backup automatique
                  </p>
                </div>
              </div>
              <Badge className="bg-white/20 text-white">Bientôt</Badge>
            </div>
          </CardContent>
        </Card>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {v2Features.map((feature, idx) => {
            const Icon = feature.icon;
            const status = statusConfig[feature.status];
            const StatusIcon = status.icon;
            
            return (
              <Card key={idx} className="bg-white border border-slate-200 hover:shadow-lg transition-all">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-purple-500 flex items-center justify-center">
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="font-bold text-slate-800">{feature.title}</h3>
                        <Badge className={cn("text-xs mt-1", status.color)}>
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {status.label}
                        </Badge>
                      </div>
                    </div>
                  </div>
                  
                  <p className="text-sm text-slate-600 mb-4">{feature.description}</p>
                  
                  {/* Progress bar */}
                  <div className="mb-3">
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="text-slate-500">Progression</span>
                      <span className="font-medium text-violet-600">{feature.progress}%</span>
                    </div>
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-violet-500 to-purple-500 rounded-full transition-all"
                        style={{ width: `${feature.progress}%` }}
                      />
                    </div>
                  </div>
                  
                  {/* Sub-items */}
                  <div className="flex flex-wrap gap-2">
                    {feature.items.map((item, itemIdx) => (
                      <span 
                        key={itemIdx}
                        className="px-2 py-1 bg-slate-100 text-slate-600 rounded text-xs"
                      >
                        {item}
                      </span>
                    ))}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Database Info */}
        <Card className="mt-8 bg-slate-50 border-slate-200">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <Database className="w-5 h-5 text-slate-600" />
              <h3 className="font-semibold text-slate-800">Données V2</h3>
            </div>
            <p className="text-sm text-slate-600 mb-4">
              Les données de la V2 seront isolées de la V1 jusqu'à la migration finale. 
              Lors de la bascule, un backup complet de la V1 sera créé automatiquement.
            </p>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <GitBranch className="w-4 h-4" />
                <span>Branche: develop</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <Database className="w-4 h-4" />
                <span>Base: isolée</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DevelopLayout>
  );
};

export default DevelopPage;
